import type {
  CreateStonePriceRuleRequest,
  StonePriceRule,
  UpdateStonePriceRuleRequest,
} from '@nucleus-commerce/api-client/admin';

import { BASE_CURRENCY } from '@/shared/catalog/base-currency';

import type {
  StonePriceRuleFormInput,
  StonePriceRuleFormValues,
} from '../schemas/stone-price-rule-form-schema';

const money = (amount: number) => ({ amount: Math.round(amount), currency: BASE_CURRENCY });

export function emptyStonePriceRuleForm(): StonePriceRuleFormInput {
  return {
    stoneType: 'lab_diamond',
    basis: 'carat',
    storeId: '',
    fromCarat: '0',
    toCarat: '',
    fromCost: money(0),
    toCost: null,
    marginPct: '',
  };
}

export function fromStonePriceRule(rule: StonePriceRule): StonePriceRuleFormInput {
  const carat = rule.basis === 'carat';
  return {
    stoneType: rule.stoneType,
    basis: rule.basis,
    storeId: rule.storeId ?? '',
    fromCarat: carat ? String(rule.rangeFrom) : '0',
    toCarat: carat ? String(rule.rangeTo) : '',
    fromCost: carat ? money(0) : money(rule.rangeFrom),
    toCost: carat ? null : money(rule.rangeTo),
    marginPct: String(rule.marginPct),
  };
}

function bounds(values: StonePriceRuleFormValues): { rangeFrom: number; rangeTo: number } {
  if (values.basis === 'carat') {
    return {
      rangeFrom: values.fromCarat === '' ? 0 : Number(values.fromCarat),
      rangeTo: Number(values.toCarat),
    };
  }
  return { rangeFrom: values.fromCost?.amount ?? 0, rangeTo: values.toCost?.amount ?? 0 };
}

export function toStonePriceRuleCreate(
  values: StonePriceRuleFormValues,
): CreateStonePriceRuleRequest {
  return {
    stoneType: values.stoneType,
    basis: values.basis,
    storeId: values.storeId || null,
    ...bounds(values),
    marginPct: Number(values.marginPct),
  };
}

/** The stone type is fixed once saved; everything else the band has is sent. */
export function toStonePriceRuleUpdate(
  values: StonePriceRuleFormValues,
): UpdateStonePriceRuleRequest {
  return {
    basis: values.basis,
    storeId: values.storeId || null,
    ...bounds(values),
    marginPct: Number(values.marginPct),
  };
}

/** The 422 for an overlapping band names `body.rangeFrom`; put it on the bound the basis shows. */
export function stoneRuleFieldPath(basis: StonePriceRule['basis']) {
  return (apiField: string): string => {
    if (apiField === 'rangeFrom') return basis === 'carat' ? 'fromCarat' : 'fromCost';
    if (apiField === 'rangeTo') return basis === 'carat' ? 'toCarat' : 'toCost';
    return apiField;
  };
}
